// The 3 × 2 table of the Dhātu lab: rows उत्तम / मध्यम / अन्य, columns एकवचन / बहुवचन (two numbers only, no dual).
// Each cell shows the core form big and, for Adults, the विकल्प forms small in terracotta. Tapping a cell, or moving
// with the arrow keys, selects that person and number.

import type { FC, KeyboardEvent, ReactNode } from 'react'
import type { GridCell } from '@/prakrit/components/grammar/grammar-grid'
import type { Num, Person } from '@/prakrit/lib/grammar/types'

import { cn } from '@/lib/utils'
import { NUMBERS, PERSONS } from '@/prakrit/lib/grammar/types'
import { NUMBER_LABEL, PERSON_LABEL, PERSON_SHORT } from '@/prakrit/lib/grammar/labels'
import { PANEL } from '@/prakrit/components/grammar/dhatu/dhatu-ui'
import { NUMBER_EN, PERSON_EN } from '@/prakrit/components/grammar/dhatu/labels'

interface VerbTableProps {
    cells: Record<Person, Record<Num, GridCell>>
    selected: { person: Person, number: Num }
    showAlt: boolean
    onSelect: (person: Person, number: Num) => void
    /** Read out to screen readers as the grid's name, e.g. "पढ · वर्तमान काल". */
    label: string
    className?: string
}

const COLS = 'grid grid-cols-[minmax(5.25rem,0.75fr)_minmax(0,1fr)_minmax(0,1fr)] gap-2 sm:grid-cols-[minmax(7rem,0.7fr)_minmax(0,1fr)_minmax(0,1fr)]'

export const VerbTable: FC<VerbTableProps> = ({ cells, selected, showAlt, onSelect, label, className }): ReactNode => {
    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        const keys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End']

        if (!keys.includes(event.key)) return

        const r = PERSONS.indexOf(selected.person)
        const c = NUMBERS.indexOf(selected.number)
        let nr = r
        let nc = c

        if (event.key === 'ArrowDown') nr = Math.min(PERSONS.length - 1, r + 1)
        else if (event.key === 'ArrowUp') nr = Math.max(0, r - 1)
        else if (event.key === 'ArrowRight') nc = Math.min(NUMBERS.length - 1, c + 1)
        else if (event.key === 'ArrowLeft') nc = Math.max(0, c - 1)
        else if (event.key === 'Home') nc = 0
        else nc = NUMBERS.length - 1

        event.preventDefault()

        const person = PERSONS[nr]
        const number = NUMBERS[nc]

        onSelect(person, number)
        event.currentTarget.querySelector<HTMLElement>(`[data-cell="${person}.${number}"]`)?.focus()
    }

    return (
        <div role='grid' aria-label={label} onKeyDown={onKeyDown} className={cn(PANEL, 'overflow-hidden', className)}>
            <div role='row' className={cn(COLS, 'items-end border-b border-border bg-surface-muted/70 px-3 py-3 sm:px-5')}>
                <span role='columnheader' className='flex flex-col text-xs leading-tight text-muted-foreground'>
                    <span lang='hi' className='hindi'>पुरुष / वचन</span>
                    <span lang='en'>person / number</span>
                </span>
                {NUMBERS.map(n => (
                    <span key={n} role='columnheader' className='flex flex-col px-3 leading-tight'>
                        <span lang='hi' className='hindi text-sm font-semibold'>{NUMBER_LABEL[n].hi}</span>
                        <span lang='en' className='text-xs text-muted-foreground'>{NUMBER_EN[n]}</span>
                    </span>
                ))}
            </div>

            {PERSONS.map(p => (
                <div key={p} role='row' className={cn(COLS, 'items-stretch border-b border-border px-3 py-2 last:border-b-0 sm:px-5')}>
                    <span role='rowheader' className='flex flex-col justify-center leading-tight'>
                        <span lang='hi' className='hindi text-sm font-semibold'>{PERSON_SHORT[p].hi}</span>
                        <span lang='en' className='text-xs text-muted-foreground'>{PERSON_EN[p]}</span>
                    </span>
                    {NUMBERS.map(n => {
                        const cell = cells[p][n]
                        const active = selected.person === p && selected.number === n
                        const alt = showAlt ? cell.alt : []
                        const many = cell.core.length > 1

                        return (
                            <div key={n} role='gridcell' className='flex'>
                                <button
                                    type='button'
                                    data-cell={`${p}.${n}`}
                                    aria-pressed={active}
                                    aria-label={`${PERSON_LABEL[p].hi} ${NUMBER_LABEL[n].hi}: ${cell.core.join(', ') || '—'}${alt.length > 0 ? `; विकल्प ${alt.join(', ')}` : ''}`}
                                    tabIndex={active ? 0 : -1}
                                    onClick={() => onSelect(p, n)}
                                    className={cn(
                                        'flex min-h-14 w-full flex-col items-start justify-center gap-0.5 rounded-xl px-3 py-2 text-left transition-colors',
                                        active ? 'bg-saffron shadow-sm' : 'hover:bg-saffron/10'
                                    )}
                                >
                                    {cell.core.length > 0 ? (
                                        <span className='flex flex-wrap gap-x-2'>
                                            {cell.core.map(f => (
                                                <span
                                                    key={f}
                                                    lang='pra'
                                                    className={cn(
                                                        'font-prakrit font-semibold',
                                                        many ? 'text-[length:var(--pra-md)] leading-[1.45]' : 'text-[length:var(--pra-lg)] leading-[1.4]',
                                                        active ? 'text-primary-foreground' : 'text-prakrit'
                                                    )}
                                                >
                                                    {f}
                                                </span>
                                            ))}
                                        </span>
                                    ) : (
                                        <span className='text-sm text-muted-foreground'>—</span>
                                    )}
                                    {alt.length > 0 && (
                                        <span lang='pra' className={cn('font-prakrit text-sm font-medium leading-snug', active ? 'text-primary-foreground/75' : 'text-terracotta')}>
                                            {alt.join(', ')}
                                        </span>
                                    )}
                                </button>
                            </div>
                        )
                    })}
                </div>
            ))}
        </div>
    )
}
